// frontend/src/components/assessment/AssessmentView.js
// Root component for the basic/assessment methodology UI.
// Two-panel split: conversation thread (left) + artifact panel (right).
//
// Spec: Product/Hawthorne/MiscFeatures/ai-form-ui-spec.md
//
// Sub-components:
//   panels/ConversationPanel.js  — left: chat thread + input
//   panels/ArtifactPanel.js      — right: tabbed artifact panel
//   artifacts/CaseStudyForm.js   — phase 2/4 form fields (step 3)
//   artifacts/ScriptEditor.js    — phase 3 script editor (step 4)

import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { VoiceChatProvider, useVoiceChatContext } from '../../contexts/VoiceChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useFeature } from '../../hooks/useFeature';
import { useReportPolling } from '../../hooks/useReportPolling';
import apiService from '../../services/apiService';
import ChatHeader from './ChatHeader';
import AssessmentPrintDocument from './AssessmentPrintDocument';
import ConversationPanel from './panels/ConversationPanel';
import ArtifactPanel from './panels/ArtifactPanel';
import { useAssessmentSession } from './hooks/useAssessmentSession';
import '../../styles/assessment.css';

const MIN_CONVERSATION_PCT = 25;
const MAX_CONVERSATION_PCT = 70;
const DEFAULT_SPLIT_PCT = 45;

// Inner component — requires VoiceChatContext to be present above it
const AssessmentViewInner = ({ conversation, messages, setMessages, onTitleUpdate }) => {
  const { user } = useAuth();
  const voiceChat = useVoiceChatContext();
  const { isMobile, setDesktopCollapsed, setMobileNavOpen } = useLayout();
  const { enabled: assessmentsViewEnabled } = useFeature('assessments_view');

  const [splitPct, setSplitPct] = useState(DEFAULT_SPLIT_PCT);
  const [isThinking, setIsThinking] = useState(false);

  const {
    session,
    sessionLoading,
    hydrateSession,
    setSessionLoadError,
    applyAIResponse,
    setFocusArtifact,
    updateCaseStudyField,
    updateGoalsField,
    updateScriptTurn,
    replaceScript,
  } = useAssessmentSession();

  const containerRef = useRef(null);
  const isDragging = useRef(false);
  // Handle to the active artifact editor so we can flush its pending autosave before
  // an AI turn (serialises the two writers — see handleSendMessage).
  const artifactRef = useRef(null);

  // Connect voice WebSocket when conversation loads
  useEffect(() => {
    voiceChat.connect();
  }, [conversation.id]); 

  // Fetch the report body. /session no longer embeds it (B.2) — this endpoint
  // is the single body exit, used on mount below and again on the transition
  // into 'completed' by the status poll. It 404s when the report isn't ready
  // or hasn't been released to the learner yet; callers treat that as "no
  // body", not as an error.
  const fetchReportBody = useCallback(async (signal) => {
    const resp = await apiService.get(
      `/api/v1/conversations/${conversation.id}/report`,
      user,
      { signal }
    );
    return resp?.data ?? null;
  }, [conversation.id, user]);

  // Hydrate session state from backend on mount
  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await apiService.get(
          `/api/v1/conversations/${conversation.id}/session`,
          user
        );
        if (!response?.success || !response?.data) return;
        hydrateSession(response.data);

        // Reporting is done but /session no longer carries the body — fetch it
        // so a refresh of a finished assessment still shows the Debrief. While
        // the subphase is still 'waiting' the poller owns the fetch instead.
        const { currentPhase, currentSubphase } = response.data;
        if (currentPhase === 'reporting' && currentSubphase !== 'waiting') {
          try {
            const reportData = await fetchReportBody();
            if (reportData) {
              applyAIResponse({
                currentPhase:     null,
                currentSubphase:  null,
                caseStudyUpdates: null,
                scriptUpdates:    null,
                reportData,
              });
            }
          } catch (bodyErr) {
            // Not ready, or not released to this learner — leave the session
            // hydrated without a body rather than failing the whole load.
            console.warn('[AssessmentView] Report body not available on mount:', bodyErr);
          }
        }
      } catch (err) {
        console.error('[AssessmentView] Failed to load session state:', err);
        setSessionLoadError(err.message);
      }
    };
    if (user) loadSession();
  }, [conversation.id, user, fetchReportBody]);

  const handleSendMessage = async (messageText, { isVoice = false } = {}) => {
    const userMessage = {
      id: Date.now().toString(),
      content: messageText,
      role: 'user',
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsThinking(true);

    // Flush any pending panel autosave BEFORE the AI turn. Otherwise the server can
    // process this message against stale script/case-study state (misplacing or dropping
    // AI-captured content), and a late autosave can then overwrite the server's write.
    // Awaiting the flush serialises the two writers.
    try {
      await artifactRef.current?.flush?.();
    } catch (err) {
      console.warn('[AssessmentView] Pre-send flush failed (continuing):', err);
    }

    try {
      const response = await apiService.post(
        `/api/v1/conversations/${conversation.id}/messages`,
        { message: messageText },
        user
      );

      if (response && response.success && response.data) {
        const aiResponseText =
          response.data?.aiResponse ||
          response.aiResponse ||
          response.response ||
          response.message ||
          response.data?.response ||
          response.data?.message;

        if (!aiResponseText) throw new Error('No AI response received');

        const aiMessage = {
          id: (Date.now() + 1).toString(),
          content: aiResponseText,
          role: 'ai',
          sender: 'ai',
          timestamp: new Date().toISOString(),
        };

        setMessages(prev => [...prev, aiMessage]);
        setIsThinking(false);

        // Apply artifact updates to session state
        applyAIResponse({
          currentPhase:     response.data?.currentPhase     || null,
          currentSubphase:  response.data?.currentSubphase  || null,
          caseStudyUpdates: response.data?.caseStudyUpdates || null,
          scriptUpdates:    response.data?.scriptUpdates    || null,
          userGoalsUpdates: response.data?.userGoalsUpdates || null,
          reportData:       response.data?.reportData       || null,
        });

        if (isVoice && voiceChat?.generateSpeech) {
          try {
            await voiceChat.generateSpeech(aiResponseText);
          } catch (err) {
            console.error('TTS error:', err);
          }
        }
      } else {
        setIsThinking(false);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setIsThinking(false);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I had trouble processing your message. Please try again.',
        role: 'ai',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    }
  };

  // Poll the dedicated report-status endpoint while waiting. When the backend
  // completes the 3-pass pipeline, update session state and surface a chat message.
  // On failure (or timeout), surface a failure state instead of polling forever.
  const [reportFailure, setReportFailure] = useState(null); // null | { reason }
  const [reportBodyError, setReportBodyError] = useState(false);
  const prevSubphaseRef = useRef(session.currentSubphase);

  useEffect(() => {
    // Clear a stale failure once the backend re-enters 'waiting' for a retried report.
    const prev = prevSubphaseRef.current;
    if (session.currentSubphase === 'waiting' && prev !== 'waiting') setReportFailure(null);
    prevSubphaseRef.current = session.currentSubphase;
  }, [session.currentSubphase]);

  const fetchReportStatus = useCallback(async (signal) => {
    try {
      const resp = await apiService.get(
        `/api/v1/conversations/${conversation.id}/report-status`,
        user,
        { signal }
      );
      const status = resp?.data?.status;
      if (status === 'completed') {
        // Fire the body fetch once, on the transition into completed —
        // polling has already stopped by the time this resolves.
        try {
          const reportData = await fetchReportBody(signal);
          return { status: 'completed', reportData };
        } catch (bodyErr) {
          if (bodyErr?.name === 'AbortError') return null;
          console.error('[AssessmentView] Report body fetch failed:', bodyErr);
          return { status: 'completed', reportData: null, bodyFetchFailed: true };
        }
      }
      if (status === 'failed') return { status: 'failed' };
      return { status: 'pending' };
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      console.error('[AssessmentView] Status check failed:', err);
      return null;
    }
  }, [conversation.id, user, fetchReportBody]);

  const retryReportBody = useCallback(async () => {
    try {
      const reportData = await fetchReportBody();
      setReportBodyError(false);
      applyAIResponse({
        currentPhase:     null,
        currentSubphase:  null,
        caseStudyUpdates: null,
        scriptUpdates:    null,
        reportData,
      });
    } catch (err) {
      console.error('[AssessmentView] Report body retry failed:', err);
      setReportBodyError(true);
    }
  }, [fetchReportBody, applyAIResponse]);

  const handleReportUpdate = useCallback((report) => {
    // With assessments_view off, the report doesn't exist as far as the learner
    // is concerned — submitting the case study is the end of the flow and the
    // workshop is their next step. State still advances (so the session leaves
    // 'waiting' and polling stops), but nothing about the report reaches chat.
    const announce = (content) => {
      if (!assessmentsViewEnabled) return;
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        content,
        role: 'ai',
        sender: 'ai',
        timestamp: new Date().toISOString(),
      }]);
    };

    if (report.status === 'completed') {
      setReportBodyError(!!report.bodyFetchFailed);
      applyAIResponse({
        currentPhase:     null,
        currentSubphase:  'active',
        caseStudyUpdates: null,
        scriptUpdates:    null,
        reportData:       report.reportData,
      });
      announce(report.bodyFetchFailed
        ? "Your assessment is complete, but we couldn't load the report just now. You can retry from the Debrief tab."
        : "Your assessment report is ready. Take a look at the Debrief tab.");
    } else if (report.status === 'failed') {
      setReportFailure({ reason: report.reason ?? 'unknown' });
    } else if (report.status === 'timed_out') {
      // Client gave up waiting; the server may still be working. Not a
      // failure — don't set reportFailure (which would permanently stop
      // re-polling on remount). The UI should read "still working."
      announce("Still working on your report — refresh in a moment to check again.");
    }
  }, [applyAIResponse, setMessages, assessmentsViewEnabled]);

  useReportPolling({
    active: session.currentSubphase === 'waiting' && !reportFailure,
    pollKey: conversation.id,
    fetchStatus: fetchReportStatus,
    onUpdate: handleReportUpdate,
  });

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (moveEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(Math.max(pct, MIN_CONVERSATION_PCT), MAX_CONVERSATION_PCT));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const showArtifact = session.focusArtifact !== null;

  // Collapse the side nav automatically when the artifact panel first appears
  useEffect(() => {
    if (showArtifact) {
      if (isMobile) {
        setMobileNavOpen(false);
      } else {
        setDesktopCollapsed(true);
      }
    }
  }, [showArtifact]); 

  return (
    <div className="assessment-view" ref={containerRef}>
      <ChatHeader
        conversation={conversation}
        currentPhase={session.currentPhase}
        onTitleUpdate={onTitleUpdate}
      />
      <AssessmentPrintDocument
        conversationTitle={conversation?.title}
        caseStudy={session.caseStudy}
        script={session.script}
        report={session.report}
        includeDebrief={assessmentsViewEnabled}
      />

      <div className="assessment-main">
        <div
          className="assessment-panel-conversation"
          style={{ flexBasis: showArtifact ? `${splitPct}%` : '100%' }}
        >
          <ConversationPanel
            messages={messages}
            onSendMessage={handleSendMessage}
            isThinking={isThinking}
          />
        </div>

        {showArtifact && (
          <>
            <div
              className="assessment-resize-handle"
              onMouseDown={handleResizeStart}
            />

            <div
              className="assessment-panel-artifact"
              style={{ flexBasis: `${100 - splitPct}%` }}
            >
              <ArtifactPanel
                ref={artifactRef}
                activeTab={session.focusArtifact}
                onTabChange={setFocusArtifact}
                session={session}
                sessionLoading={sessionLoading}
                onCaseStudyFieldChange={updateCaseStudyField}
                onGoalsFieldChange={updateGoalsField}
                onScriptTurnChange={updateScriptTurn}
                onScriptReplace={replaceScript}
                onDone={handleSendMessage}
                reportFailure={reportFailure}
                reportBodyError={reportBodyError}
                onRetryReportBody={retryReportBody}
                conversationId={conversation.id}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

AssessmentViewInner.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    type: PropTypes.string,
    methodology: PropTypes.string,
  }).isRequired,
  messages: PropTypes.array.isRequired,
  setMessages: PropTypes.func.isRequired,
  onTitleUpdate: PropTypes.func.isRequired,
};

// Outer wrapper provides VoiceChatContext (required by ChatInput)
const AssessmentView = (props) => (
  <VoiceChatProvider conversationId={props.conversation.id}>
    <AssessmentViewInner {...props} />
  </VoiceChatProvider>
);

AssessmentView.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    type: PropTypes.string,
    methodology: PropTypes.string,
  }).isRequired,
  messages: PropTypes.array.isRequired,
  setMessages: PropTypes.func.isRequired,
  onTitleUpdate: PropTypes.func.isRequired,
};

export default AssessmentView;